import React, { useEffect, useState } from "react";
import { useQuery, useMutation } from "@apollo/client/react";
import gql from "graphql-tag";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCaretUp,
  faCaretDown,
  faXmark,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { HOME_WIDGET_OPTIONS, DEFAULT_HOME_WIDGETS } from "../config/homeWidgets";

const GET_HOME_WIDGETS = gql`
  query {
    getHomeWidgets
  }
`;

const SET_HOME_WIDGETS = gql`
  mutation setHomeWidgets($widgets: [String]!) {
    setHomeWidgets(widgets: $widgets)
  }
`;

function HomeWidgetSettings() {
  const { loading, data } = useQuery(GET_HOME_WIDGETS);
  const [setHomeWidgets, { loading: saving }] = useMutation(SET_HOME_WIDGETS, {
    update(cache, { data: mutationData }) {
      cache.writeQuery({
        query: GET_HOME_WIDGETS,
        data: { getHomeWidgets: mutationData.setHomeWidgets },
      });
    },
  });

  const [widgetOrder, setWidgetOrder] = useState(DEFAULT_HOME_WIDGETS);
  const [hasLoadedPreferences, setHasLoadedPreferences] = useState(false);
  const [savedMessageVisible, setSavedMessageVisible] = useState(false);

  useEffect(() => {
    if (!loading && data && !hasLoadedPreferences) {
      setWidgetOrder(
        data.getHomeWidgets && data.getHomeWidgets.length > 0
          ? data.getHomeWidgets
          : DEFAULT_HOME_WIDGETS
      );
      setHasLoadedPreferences(true);
    }
  }, [loading, data, hasLoadedPreferences]);

  const getWidgetLabel = (key) => {
    const widget = HOME_WIDGET_OPTIONS.find((w) => w.key === key);
    return widget ? widget.label : key;
  };

  const hiddenWidgets = HOME_WIDGET_OPTIONS.filter(
    (w) => !widgetOrder.includes(w.key)
  );

  const moveWidget = (index, direction) => {
    const newOrder = [...widgetOrder];
    const targetIndex = index + direction;

    if (targetIndex < 0 || targetIndex >= newOrder.length) {
      return;
    }

    [newOrder[index], newOrder[targetIndex]] = [
      newOrder[targetIndex],
      newOrder[index],
    ];
    setWidgetOrder(newOrder);
  };

  const hideWidget = (key) => {
    setWidgetOrder(widgetOrder.filter((w) => w !== key));
  };

  const showWidget = (key) => {
    setWidgetOrder([...widgetOrder, key]);
  };

  const handleSave = async () => {
    await setHomeWidgets({ variables: { widgets: widgetOrder } });
    setSavedMessageVisible(true);
    setTimeout(() => setSavedMessageVisible(false), 2000);
  };

  if (loading) {
    return <p>Loading home widget preferences...</p>;
  }

  return (
    <div className="home-widget-settings">
      <ul className="home-widget-settings-list">
        {widgetOrder.map((key, index) => (
          <li key={key} className="home-widget-settings-item">
            <span className="home-widget-settings-label">
              {getWidgetLabel(key)}
            </span>
            <span className="home-widget-settings-actions">
              <button
                type="button"
                onClick={() => moveWidget(index, -1)}
                disabled={index === 0}
              >
                <FontAwesomeIcon icon={faCaretUp} />
              </button>
              <button
                type="button"
                onClick={() => moveWidget(index, 1)}
                disabled={index === widgetOrder.length - 1}
              >
                <FontAwesomeIcon icon={faCaretDown} />
              </button>
              <button type="button" onClick={() => hideWidget(key)}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </span>
          </li>
        ))}
      </ul>

      {hiddenWidgets.length > 0 && (
        <React.Fragment>
          <h4 className="home-widget-settings-subheading">Hidden widgets</h4>
          <ul className="home-widget-settings-list">
            {hiddenWidgets.map((widget) => (
              <li key={widget.key} className="home-widget-settings-item">
                <span className="home-widget-settings-label">
                  {widget.label}
                </span>
                <span className="home-widget-settings-actions">
                  <button type="button" onClick={() => showWidget(widget.key)}>
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </React.Fragment>
      )}

      <button
        type="button"
        className="home-widget-settings-save"
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? "Saving..." : "Save"}
      </button>
      {savedMessageVisible && (
        <span className="home-widget-settings-saved">Saved!</span>
      )}
    </div>
  );
}

export default HomeWidgetSettings;
